import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getGettingStartedStatus } from "../../helpers/backend_helper";
import { getAuthenticatedUser } from "../../helpers/auth-session";
import ContextualGuide, { CompletionToast } from "./ContextualGuide";
import { gettingStartedActionPath, onboardingSummary } from "./onboardingRules";
import "./Onboarding.scss";

export const OnboardingContext = createContext(null);

export default function OnboardingProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [gettingStarted, setStatus] = useState(null);
  const [gettingStartedLoading, setLoading] = useState(true);
  const [gettingStartedError, setError] = useState("");
  const [dismissedGuides, setDismissedGuides] = useState([]);
  const [activeGuideId, setActiveGuideId] = useState(null);
  const [completion, setCompletion] = useState(null);
  const request = useRef(0);
  const mounted = useRef(true);
  const previousMilestones = useRef({});
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current += 1;
    };
  }, []);

  const refreshGettingStarted = useCallback(async () => {
    const id = ++request.current;
    if (location.pathname === "/switch-firm" || getAuthenticatedUser()?.requiresFirmSelection) {
      setStatus(null);
      setError("");
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const response = await getGettingStartedStatus();
      if (!response?.status || !response.data) throw new Error("Unavailable");
      if (mounted.current && id === request.current) {
        setStatus(response.data);
        setError("");
      }
      return response.data;
    } catch {
      if (mounted.current && id === request.current)
        setError(
          "We couldn’t refresh your setup progress. Your saved work is safe. Please try again."
        );
      return null;
    } finally {
      if (mounted.current && id === request.current) setLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    refreshGettingStarted();
  }, [location.pathname, refreshGettingStarted]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "hidden") refreshGettingStarted();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("piers:readiness-changed", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("piers:readiness-changed", refresh);
    };
  }, [refreshGettingStarted]);
  const markSetupComplete = useCallback(() => {
    // Completion has already been saved; invalidate older in-flight status reads.
    request.current += 1;
    setStatus((current) => current ? {
      ...current,
      onboardingComplete: true,
      preference: { ...current.preference, tourCompleted: true },
    } : current);
    setLoading(false);
  }, []);

  const openGettingStarted = useCallback(
    () => navigate("/piers/getting-started"),
    [navigate]
  );
  const launchAction = useCallback(
    (action, milestone) => {
      const path = gettingStartedActionPath(action);
      if (path) {
        setCompletion(null);
        setActiveGuideId(milestone?.id || null);
        navigate(path, { state: { onboardingStep: milestone?.id || null } });
      }
    },
    [navigate]
  );
  const summary = useMemo(
    () => onboardingSummary(gettingStarted),
    [gettingStarted]
  );
  const activeStep = location.state?.onboardingStep || activeGuideId;
  const guide = gettingStarted?.milestones?.find(
    (item) =>
      item.id === activeStep &&
      item.status !== "completed" &&
      gettingStartedActionPath(item.action)?.split("?")[0] ===
        location.pathname &&
      !dismissedGuides.includes(item.id)
  );
  useEffect(() => {
    const current = Object.fromEntries(
      (gettingStarted?.milestones || []).map((item) => [item.id, item.status])
    );
    const activeId = activeStep;
    if (
      activeId &&
      previousMilestones.current[activeId] &&
      previousMilestones.current[activeId] !== "completed" &&
      current[activeId] === "completed"
    ) {
      const milestone = gettingStarted.milestones.find(
        (item) => item.id === activeId
      );
      setCompletion({ milestone, nextAction: gettingStarted.nextAction });
      setActiveGuideId(null);
    }
    previousMilestones.current = current;
  }, [gettingStarted, activeStep]);
  const value = useMemo(
    () => ({
      gettingStarted,
      preference: gettingStarted?.preference,
      gettingStartedLoading,
      gettingStartedError,
      refreshGettingStarted,
      markSetupComplete,
      launchAction,
      openGettingStarted,
      onboardingSummary: summary,
      setupAssistantOpen: location.pathname === "/piers/getting-started",
      openSetupAssistant: openGettingStarted,
      closeSetupAssistant: () => {},
      // Compatibility for existing Help links: all onboarding entry points use the full-page workspace.
      showGettingStartedWelcome: openGettingStarted,
      launchTour: openGettingStarted,
      continueTour: openGettingStarted,
      gettingStartedVisible: location.pathname === "/piers/getting-started",
      hideGettingStartedForSession: () => {},
    }),
    [
      gettingStarted,
      gettingStartedLoading,
      gettingStartedError,
      refreshGettingStarted,
      markSetupComplete,
      launchAction,
      openGettingStarted,
      summary,
      location.pathname,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {guide && (
        <ContextualGuide
          milestone={guide}
          onDismiss={() => {
            setDismissedGuides((current) => [...current, guide.id]);
            setActiveGuideId(null);
          }}
        />
      )}
      {children}
      {completion && (
        <CompletionToast
          milestone={completion.milestone}
          nextAction={completion.nextAction}
          onContinue={() => launchAction(completion.nextAction)}
          onDismiss={() => setCompletion(null)}
        />
      )}
    </OnboardingContext.Provider>
  );
}
